import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Card, CardContent, CardActions, Avatar } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '@/components/ButtonLink'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { ProjectDetailsPendingComponent } from '@/features/admin/projects/detail/components/ProjectDetailsPendingComponent'
import { ProjectDetailsTableLayout } from '@/features/admin/projects/detail/components/ProjectDetailsTableLayout'
import { useProjectDetailsUserColumns } from '@/features/admin/projects/detail/helper/projectDetailsUserColumns'
import {
    trackingKindsQueryOptions,
    useTrackingKinds,
} from '@/features/edit-trackings/queries/useTrackingKinds'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { stringifyId } from '@/helper/id'
import {
    projectDetailsQueryOptions,
    useProjectDetails,
} from '@/queries/useProjectDetails'
import { availablePageSizes } from '@/schemas/searchParams/searchParamsPaginationSchema'

const seleniumPrefix = 'view-project'

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const { t } = useTranslation()
    const columns = useProjectDetailsUserColumns()
    const {
        data: { project, users },
    } = useProjectDetails(id, DeletedItemSelection.BOTH)

    const { data: trackingKinds } = useTrackingKinds()

    return (
        <Card>
            <CardContent>
                <ProjectDetailsTableLayout
                    projectName={project?.name}
                    description={project?.description}
                    startDate={
                        project?.startAt ?
                            t('common.dateOnly.short', {
                                value: timestampDate(project.startAt),
                            })
                        :   ''
                    }
                    endDate={
                        project?.endAt ?
                            t('common.dateOnly.short', {
                                value: timestampDate(project.endAt),
                            })
                        :   ''
                    }
                    trackingType={
                        project?.defaultTrackingKindId ?
                            (trackingKinds.trackingKinds.find(
                                kind =>
                                    kind.id &&
                                    stringifyId(kind.id) ===
                                        stringifyId(
                                            project.defaultTrackingKindId
                                        )
                            )?.name ?? '')
                        :   ''
                    }
                    deleted={
                        project?.timestamps?.deletedAt ?
                            t('common.yes')
                        :   t('common.no')
                    }
                    color={
                        <Avatar
                            style={{
                                backgroundColor: project?.displayColor,
                                width: 24,
                                height: 24,
                            }}
                        >
                            {' '}
                        </Avatar>
                    }
                />
                <DataGrid
                    rows={users}
                    columns={columns}
                    pageSizeOptions={availablePageSizes}
                    paginationMode={'client'}
                    sortingMode={'client'}
                    getRowId={({ userName }) => userName}
                    sx={{ marginTop: 2 }}
                />
            </CardContent>
            <CardActions style={{ justifyContent: 'flex-end' }}>
                <ButtonLink
                    to={'/admin/projects'}
                    data-selenium={`${seleniumPrefix}-back`}
                >
                    {t('common.back')}
                </ButtonLink>
                <ButtonLink
                    params={{ id }}
                    to={'/admin/projects/$id/edit'}
                    variant={'outlined'}
                    data-selenium={`${seleniumPrefix}-edit`}
                >
                    {t('common.edit')}
                </ButtonLink>
            </CardActions>
        </Card>
    )
}

export const Route = createFileRoute('/__base-layout/admin/projects/$id/view')({
    component: RouteComponent,
    beforeLoad: async ({
        context: { user, queryClient, grpcClients },
        params: { id },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(
                projectDetailsQueryOptions(
                    grpcClients,
                    id,
                    DeletedItemSelection.BOTH
                )
            ),
            queryClient.ensureQueryData(trackingKindsQueryOptions(grpcClients)),
        ])
    },
    pendingComponent: ProjectDetailsPendingComponent,
})
